import React, { Fragment, memo, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Box } from '@material-ui/core'
import { withRouter } from 'react-router-dom'

import { AmbassadorsWrapper } from 'Components/City/Page/Ambassadors/AmbassadorsWrapper'
import DataFetchingTableTable from 'Components/DataFetchingTableTable'
import { Ticked } from 'Components/Ticked'
import { StatusLabel } from 'Components/StatusLabel'
import { StarRating } from 'Components/StarRating'
import { LinkView } from 'Components/Link/View'
import { LinkEdit } from 'Components/Link/Edit'
import { ButtonDeleteInline } from 'Components/Buttons/ButtonDeleteInline'

import {
  showReduxModal,
  hideReduxModal,
  resetReduxModal,
} from 'Reducers/UI/actions'
import { numberWithCommas, formatPhoneNumber } from 'Utils/Formatters'
import { getBAName } from 'Utils/User'
import { futureMissionsCount } from 'Utils/Filters'
import {
  dropTimeZoneFromDate,
  browserSafeNewDate,
  toLocaleNonAmericanDateString,
} from 'shared/libraries/utils/date'
import { useAmbassadorsRequest } from './gql'
import { MISSION_STATUS_ENUM } from '../../../../shared/constants'

const cache = {}
function AmbassadorsListView({ cityId, match: { url } }) {
  const { getAmbassadors, deleteAmbassador } = useAmbassadorsRequest(cityId)
  const dispatch = useDispatch()
  const [dataStale, setDataStale] = useState(false)

  const fetchData = useCallback(
    async args => {
      var { filter, first, orderBy, skip, Status, Carrying, Withhold } = args
      // Terminating is a special combination of blocked but has upcoming missions
      let hasMissionsBooked
      var statuses
      if (Status == 'TERMINATING') {
        statuses = ['BLOCKED']
        hasMissionsBooked = true
      } else if (Status) {
        statuses = [Status]
      }

      if (orderBy == 'STARRATING_ASC') {
        orderBy = 'RATING_ASC'
      } else if (orderBy == 'STARRATING_DESC') {
        orderBy = 'RATING_DESC'
      }

      const reqArgs = {
        first,
        hasInventory: Carrying != null ? Carrying : undefined,
        statuses,
        search: filter ? filter : undefined,
        orderBy,
        skip,
        hasMissionsBooked,
        isWithholdPay: Withhold != null ? Withhold : undefined,
      }
      const key = JSON.stringify(reqArgs)
      if (!dataStale && cache[cityId + key]) {
        return cache[cityId + key]
      }

      const { ambassadors, totalCount } = await getAmbassadors(reqArgs, {
        isFresh: true,
      })

      cache[cityId + key] = {
        data: ambassadors.map(n => {
          // Map status to a more readable version
          var status = n.status ? n.status.toLowerCase() : ''

          if (status == 'blocked' && futureMissionsCount(n.missions.nodes)) {
            status = 'terminating'
          }

          return [
            getBAName({
              firstName: n.firstName,
              lastName: n.lastName,
              isTeamLeader: n.isTeamLeader,
            }),
            toLocaleNonAmericanDateString(
              browserSafeNewDate(dropTimeZoneFromDate(n.memberSince)),
            ),
            <StarRating id={n.id} score={n.rating} uniqueId={n.id} />,
            formatPhoneNumber(n.mobilePhone),
            n.email,
            // Total number of missions completed
            numberWithCommas(
              !n.missions.nodes.length
                ? 0
                : n.missions.nodes.filter(
                    item =>
                      item.status === MISSION_STATUS_ENUM['UNDER_REVIEW'] ||
                      item.status === MISSION_STATUS_ENUM['COMPLETED'] ||
                      item.status === MISSION_STATUS_ENUM['PAID'],
                  ).length,
            ),
            // Total number of flyers distributed
            numberWithCommas(
              n.missions.nodes.reduce(
                (cum, m) => cum + (m.totalFlyersDistributed || 0),
                0,
              ),
            ),
            <Ticked ticked={n.itemCount > 0} />,
            <Ticked ticked={n.documentsProvided} />,
            <StatusLabel status={status} />, // TODO Terminating
            <div className="actions-cell">
              <LinkView to={`${url}/${n.id}`} />
              <LinkEdit to={`${url}/${n.id}/edit`} />
              <ButtonDeleteInline
                onClickHandler={() =>
                  dispatch(
                    showReduxModal({
                      title: 'Confirmation needed',
                      onConfirm: async () => {
                        await deleteAmbassador(n.id)
                        setDataStale(true)
                        hideReduxModal()
                        resetReduxModal()
                      },
                      onCancel: () => {
                        hideReduxModal()
                        resetReduxModal()
                      },
                      children: (
                        <Box
                          display="flex"
                          flexWrap="wrap"
                          justifyContent="center"
                          mt={3}
                          mb={3}
                        >
                          Are you sure you want to delete {n.firstName}{' '}
                          {n.lastName}?
                        </Box>
                      ),
                    }),
                  )
                }
              />
            </div>,
          ]
        }),
        totalCount,
      }

      return cache[cityId + key]
    },
    [dataStale],
  )

  return (
    <Fragment>
      <AmbassadorsWrapper>
        <DataFetchingTableTable
          sortableHeaders={['Name', 'Signup Date', 'Star Rating']}
          showSearch={true}
          filters={filters}
          header={header}
          fetchData={fetchData}
          // Kinda Voodoo how this prop works. We're using it here to refresh
          // the data once a BA is deleted
          // Timeout need for cache work correct (line 63)
          refetch={() => {
            if (dataStale) {
              setTimeout(() => setDataStale(false), 100)
              return true
            }
            return false
          }}
        />
      </AmbassadorsWrapper>
    </Fragment>
  )
}

const filters = [
  {
    label: 'Status',
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Blocked', value: 'BLOCKED' },
      { label: 'Deleted', value: 'DELETED' },
      { label: 'Pending', value: 'PENDING' },
      { label: 'Terminating', value: 'TERMINATING' },
      { label: 'Pre access', value: 'PRE_ACCESS' },
      { label: 'Waiting for review', value: 'WAITING_FOR_REVIEW' },
    ],
  },
  {
    label: 'Carrying',
    options: [{ label: 'No', value: false }, { label: 'Yes', value: true }],
  },
  {
    label: 'Withhold',
    options: [
      { label: 'Withhold OFF', value: false },
      { label: 'Withhold ON', value: true },
    ],
  },
]

const header = [
  'Name',
  'Signup Date',
  'Star Rating',
  'Phone Number',
  'Email',
  '# Missions',
  'Total # Flyers',
  'Has Merchandise?',
  'ID Document Provided',
  'Status',
  'Actions',
]

export default memo(withRouter(AmbassadorsListView))



// WEBPACK FOOTER //
// ./src/Containers/City/Page/Ambasadors/AmbassadorsListView.js